<template>
  <div v-show="!hidden">
    <label>
      <!-- Checkboxes -->
      <div
        v-if="['checkbox'].includes($attrs.type)"
        class="custom-control custom-checkbox"
      >
        <input
          ref="input"
          :checked="value"
          @input="$emit('update', $event.target.checked)"
          :type="$attrs.type"
          v-bind="$attrs"
          v-on="$listeners"
          class="custom-control-input"
        />
        <span class="custom-control-label">
          <slot name="label">{{ $attrs.label }}</slot>
          <span v-if="required" class="text-danger" aria-hidden>*</span>
        </span>
      </div>

      <!-- Everything else -->
      <template v-else>
        <slot name="label">{{ $attrs.label }}</slot>
        <span v-if="required" class="text-danger" aria-hidden>*</span>

        <b-input-group>
          <div v-if="prependIcon" class="input-group-prepend">
            <i class="input-group-text" :class="prependIcon"></i>
          </div>

          <component
            ref="input"
            :is="component"
            :value.prop="value"
            v-bind="$attrs"
            v-on="$listeners"
            @input="onInput"
            @change="onInput"
            :class="computedClass"
            :aria-invalid="!!errors"
            :rows="$attrs.type === 'textarea' && 3"
          >
            <template v-if="$attrs.type === 'textarea'">{{ value }}</template>
            <option
              v-for="(option, i) in computedOptions"
              :key="i"
              v-bind="option"
              :selected="option.value == value"
            >
              {{ option.label }}
            </option>
          </component>
        </b-input-group>

        <small v-if="errors" class="text-danger">{{ errors }}</small>
      </template>
    </label>
  </div>
</template>

<script>
const baseClass = "nanome-input__input";

export default {
  inheritAttrs: false,

  model: {
    event: "update"
  },

  props: {
    value: [String, Number, Boolean],
    hidden: Boolean,
    required: Boolean,
    options: Array,
    validation: Object,
    errorMap: Object,
    prependIcon: String,
    maxRows: {
      type: [Number, String],
      default: null
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: Infinity
    }
  },

  computed: {
    component() {
      const { type } = this.$attrs;
      const map = {
        select: "select",
        textarea: "textarea"
      };
      return map[type] || "input";
    },

    computedClass() {
      const { type } = this.$attrs;

      return {
        [baseClass]: true,
        [`${baseClass}--${type}`]: !!type,
        "custom-select": type === "select",
        "form-control": type !== "select"
      };
    },

    computedOptions() {
      if (!this.options) return null;

      return this.options.map(option => {
        // Each option should be an object with at least value and label which we can bind to later
        option = typeof option === "object" ? option : { value: option };
        option.label = option.label || option.value;
        option.name = this.$attrs.name;
        return option;
      });
    },

    errors() {
      if (!this.validation || !this.validation.$error) return "";

      const { $params } = this.validation;
      const errorMap = {
        required: "This field is required.",
        requiredIf: "This field is required.",
        minLength: `Cannot be shorter than ${$params.minLength &&
          $params.minLength.min} characters.`,
        maxLength: `Cannot be longer than ${$params.maxLength &&
          $params.maxLength.max} characters.`,
        minValue: `Cannot be less than ${$params.minValue &&
          $params.minValue.min}.`,
        maxValue: `Cannot be greater than ${$params.maxValue &&
          $params.maxValue.max}.`,
        between: `Value must be between ${$params.between &&
          $params.between.min} and ${$params.between && $params.between.max}.`,
        email: `Must be a valid email address.`,
        numeric: `Must be a number.`,
        sameAs: `Passwords must match.`,
        password: `Must have at least one uppercase, lowercase, and special character.`,
        ...this.errorMap
      };

      const errors = [];
      // Loop over each validator
      Object.keys($params).forEach(key => {
        // Check if this validator entry is invalid
        if (!this.validation[key]) {
          // Add custom error message based on validator type
          const errorType = $params[key] ? $params[key].type : key;
          const error = errorMap[errorType] || `Validation error: ${errorType}`;
          errors.push(error);
        }
      });

      if (!errors.length) return "This field has some errors";

      return errors.join(" ");
    }
  },

  watch: {
    value: {
      handler: "recalcTextareaHeight",
      immediate: true
    }
  },

  methods: {
    focus() {
      this.$refs.input.focus();
    },

    onInput({ target }) {
      if (this.$attrs.type === "number") {
        const value = Math.max(this.min, Math.min(this.max, +target.value));
        target.value = value || 0;
        this.$emit("update", +target.value);
      } else {
        this.$emit("update", target.value);
      }
    },

    async recalcTextareaHeight() {
      if (this.$attrs.type !== "textarea") return;
      await this.$nextTick();

      // Resize textarea to fit content
      const textarea = this.$refs.input;
      textarea.style.height = "auto";
      const { offsetHeight, clientHeight, scrollHeight } = textarea;
      const borderHeight = offsetHeight - clientHeight;
      textarea.style.height = scrollHeight + borderHeight * 3 + "px";
    }
  }
};
</script>

<style lang="scss">
.nanome-input {
  &__input {
    &--textarea {
      max-height: 300px;
    }
  }
}
</style>
